import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FaShoppingBasket, FaRegCalendarAlt, FaRegAddressCard, FaCheck, FaUserAlt, FaPhoneAlt, FaEnvelope, FaMapPin, FaRegAddressBook, } from "react-icons/fa";
import axios from "axios";
import { Select, MenuItem, Button, Table, TableHead, TableCell, TableRow, TableBody } from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CloseIcon from '@mui/icons-material/Close';
import { message } from "antd";
import styles from './OrderSummary.module.css';
import APIURL, { IMAGE_BASE_URL } from "../api/apiClient";
import Loader from "./Loader";


const OrderSummary = () => {
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [editedStatus, setEditedStatus] = useState('');
  const [orderSummary, setOrderSummary] = useState({
    order_id: '',
    created_at: '',
    user_name: '',
    order_total: '',
    payment_status: '',
    order_status: '',
    payment_method: '',
    address: '',
    items: [] // Store items as an array
  });

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${APIURL.baseURL}/fetchorderid.php?id=${id}`);
        const data = response.data;

        if (data && !data.error) {
          const baseImageUrl = `${IMAGE_BASE_URL}`; // prepend your base URL

          const items = data.item_ids.map((itemId, index) => {
            const trimmedId = itemId.trim();
            return {
              item_id: trimmedId,
              description: data.descriptions[index]?.trim() || "Unknown Item",
              quantity: data.total_quantity[index]?.trim() || "0",
              price_per_quantity: data.price_per_quantity[index]?.trim() || "0",
              total_price: data.total_price[index]?.trim() || "0",
              image: data.image[trimmedId] ? `${baseImageUrl}${data.image[trimmedId]}` : null,
            };
          });

          setOrderSummary({
            ...data,
            items,
          });
        } else {
          console.error(data.error || "No data received");
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);


  // Called when user selects a new status from dropdown
  const handleSelectChange = (order_id, newStatus) => {
    // Update local state only
    setEditedStatus((prevState) => ({
      ...prevState,
      [order_id]: newStatus
    }));

    console.log(`Order ID: ${order_id}, Selected New Status: ${newStatus}`);
  };

  // Called when user clicks the Submit button for a specific order
  const handleSelectSubmit = async (order_id) => {
    const newStatus = editedStatus[order_id]; // Get the updated status
    if (!newStatus) {
      console.warn('No changes to submit');
      return;
    }

    // Confirm before submitting
    const confirm = window.confirm("Would you like to change the order status?");
    if (!confirm) {
      console.log('User canceled the status change.');
      return;
    }

    try {
      console.log('Submitting:', { order_id, new_status: newStatus });

      // Call backend API
      const response = await fetch(`${APIURL.baseURL}/updateorderstatus.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id, new_status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      const data = await response.json();
      if (data.success) {
        message.success(`Order Status Updated Successfully for Order ID: ${order_id}`);

        // Update the order summary locally
        setOrderSummary((prevDetails) => ({
          ...prevDetails,
          order_status: newStatus,
        }));

        // Remove the edited status entry for this order
        setEditedStatus((prevState) => {
          const { [order_id]: removed, ...rest } = prevState;
          return rest;
        });
      } else {
        message.error(`Failed to update order status: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Error updating order status');
    }
  };

  
            // ✅ Full screen loader while fetching
        if (loading) {
          return <Loader type="full" loading={loading} />;
        }

  return (
    <>
      {/* <Loader type="full" loading={loading} /> */}

      <div className="container-fluid">
        <div className="row mt-3">
          {/* Left Section - Order Details (Top) and Items (Below) */}
          <div className="col-sm-8">
            {/* Order Details */}
            <div className="card mb-4">
              <div className="card-body">
                {/* Header row - only Order Details with full border bottom */}
                <div style={{ borderBottom: '1px solid #ccc', paddingBottom: '10px', marginBottom: '10px' }}>
                  <h4 className="mb-0">
                    <FaShoppingBasket size={30} /> Order Details
                  </h4>
                </div>

                {/* Order ID and Date aligned to the right below the border */}
                <div style={{ textAlign: 'right', marginBottom: '10px' }}>
                  <h5 className="mb-0"><strong>Order ID:</strong> {id}</h5>
                  <h6 className="text-muted mb-0"><FaRegCalendarAlt /> {orderSummary.created_at}</h6>
                </div>

                {/* Status and other info */}
                <p className="pt-3"><strong>Order Status:</strong> <span className={styles.orderStatus}>{orderSummary.order_status}</span></p>
                <p className="pt-2"><strong>Payment Status:</strong> {orderSummary.payment_status}</p>
                <p className="pt-2"><strong>Order Type:</strong> {orderSummary.order_type || '--'}</p>
                <p className="pt-2"><strong>Payment Method:</strong> {orderSummary.payment_method || '--'}</p>
              </div>
            </div>



            {/* Items (Below Order Details) */}
            <div className="card">
              <div className="card-body">
                {orderSummary.items.length > 0 ? (
                  <Table className="table">
                    {/* Table Header */}
                    <TableHead sx={{ backgroundColor: '#e0e0e0', height: '50px' }}>
                      <TableRow sx={{ borderBottom: 'none', }}>
                        <TableCell sx={{ width: '10%' }}><b>No. </b></TableCell>
                        <TableCell sx={{ width: '70%' }}><b>Item Details</b></TableCell>
                        <TableCell><b>Price</b></TableCell>
                      </TableRow>
                    </TableHead>

                    {/* Table Body */}
                    <TableBody sx={{ minHeight: 200 }}>
                      {orderSummary.items.map((item, index) => (
                        <TableRow key={index} sx={{ minHeight: "100px" }}> {/* Ensure consistent row height */}
                          {/* Serial Number */}
                          <TableCell>{index + 1}</TableCell>

                          {/* Item Details */}
                          <TableCell>
                            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                              {/* Image or Placeholder */}
                              <div style={{ minWidth: "100px", textAlign: "center" }}>
                                {item.image ? (
                                  <img
                                    src={item.image.trim()}
                                    alt={item.description}
                                    onError={(e) => console.error("Image Load Error:", item.image, e)}
                                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "5px", marginRight: "15px" }}
                                  />
                                ) : (
                                  <div style={{ width: "100px", height: "100px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0", borderRadius: "5px" }}>
                                    No image
                                  </div>
                                )}
                              </div>

                              {/* Description + Quantity & Price */}
                              <div style={{ fontSize: "15px", }}>
                                {item.description}<br />
                                <span style={{ fontSize: "12px", color: "gray" }}>
                                  {item.quantity} × {item.price_per_quantity}
                                </span>
                              </div>
                            </div>
                          </TableCell>

                          {/* Total Price */}
                          <TableCell>{item.total_price}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p>No items found</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Section - Order Status (Top) and Customer Details (Below) */}
          <div className="col-sm-4">
            {/* Order Status - Top Right */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="mb-4 border-bottom pb-2">
                  <h5>Order Status</h5>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Select
                    value={editedStatus[orderSummary.order_id] || orderSummary.order_status}
                    onChange={(e) => handleSelectChange(orderSummary.order_id, e.target.value)}
                    style={{
                      width: "100%",
                      height: 40,
                      flex: 1 // This makes the Select take up available space
                    }}
                  >
                    <MenuItem value="Processing">Processing</MenuItem>
                    <MenuItem value="Shipped">Shipped</MenuItem>
                    <MenuItem value="Delivered">Delivered</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>

                  <Button
                    style={{
                      borderRadius: "5px",
                      padding: '8px 15px',
                      fontSize: '18px',
                      border: 'none',
                      backgroundColor: "#007bff",
                      color: "#fff",
                      height: 40, // Match the Select height
                      minWidth: 40, // Ensure consistent width
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => handleSelectSubmit(orderSummary.order_id)}
                  >
                    <FaCheck />
                  </Button>
                </div>
              </div>
            </div>

            {/* Customer Details - Below Order Status */}
            <div className="card ">
              <div className="card-body">
                <h5 style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #ccc',  // Add this for border bottom
                  paddingBottom: '8px',           // Add some spacing below the text
                  marginBottom: '20px'            // Add margin if needed
                }}>
                  <FaRegAddressBook style={{
                    color: '#FF0000',
                    borderRadius: '10px',
                    width: '50px',
                    height: '40px',
                    marginLeft: '10px',
                    marginRight: '10px'
                  }} />
                  Customer Details
                </h5>
                <br />
                <div style={{ display: 'flex', alignItems: 'left', gap: 20 }}>
                  {/* <FaRegAddressCard style={{ color: 'lightblue', borderRadius: '10px', width: '80px', height: '60px' }} /> */}
                  <div>
                    <h6><strong className="text-capitalize">{orderSummary.full_name}</strong></h6>
                    <h6 className="pt-2"> <FaPhoneAlt className="me-2 text-success" /> {orderSummary.mobile_number}</h6>
                    <h6 className="pt-2"><FaEnvelope className="me-2 text-success" /> {orderSummary.email}</h6>
                    <h6 className="pt-2"><FaMapPin size={20} className="me-2 text-success" /> <strong>Street:</strong> {orderSummary.street_address}</h6>
                    <h6 className="pt-2 ms-5"><strong>City:</strong> {orderSummary.city}</h6>
                    <h6 className="pt-2 ms-5"><strong>State:</strong> {orderSummary.state}</h6>
                    <h6 className="pt-2 ms-5"><strong>PinCode:</strong> {orderSummary.pin_code}</h6>
                    <h6 className="pt-2 ms-5"><strong>Land Mark:</strong> {orderSummary.landmark}</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderSummary;